import { FC, Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Transition,
  Dialog,
  TransitionChild,
  DialogPanel,
  DialogTitle,
  Checkbox,
} from "@headlessui/react";
import { useData } from "data/data-provider";
import { cancelBooking, retriveBooking } from "api/booking/booking";
import { getCustomerFiles, uploadCustomerFiles } from "api/customer/customer";
import { readOdometer, OdometerReadResult } from "api/vehicles/vehicle";
import { getTripPhotos } from "api/booking/booking";
import toast from "react-hot-toast";
import { globalJson } from "global/global_json";
import {
  getPrivacyPolicyContent,
  sendRentalAgreement,
} from "api/rental-agreement/rental-agreement";
import {
  CameraIcon,
  Squares2X2Icon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import moment from "moment";
import { trackBookingStep } from "analytics/bookingEvents";
import {
  CreateStripeVerification,
  CreateVouchVerification,
} from "api/stripe/stripe";
import { CreateCanopyVerification } from "api/Canopy/canopy";
import { CanopyPayload } from "api/types";
import Badge from "shared/Badge/Badge";
import { getBonzahPDF, getBonzahProducts } from "api/bonzah/bonzah";
import { bonzahCoverNotes } from "./bonzahCoverNote";
import {
  formatDateToHumanFirendly,
  formatTimeToHumanFirendly12Hour,
  formatTimeToHumanFirendly24Hour,
  formatPhoneLabel,
  formatPrice,
  renterError,
  cash,
  chargedNow,
  isBooking,
  isLongTerm,
  photoUrl,
  policySections,
  readPolicyContent,
  uses24Hour,
} from "@1-now/booking-core";
import { Slide } from "react-slideshow-image";
import PhoneNumberDisplay from "utils/PhoneFormat";
import EditBooking from "../EditBooking/EditBooking";
import TripInspectionModal from "./TripInspectionModal";
import PreviousBookings from "../PreviousBookings/PreviousBooking";
import { reservationUnpaid } from "./reservationUnpaid";
import UnpaidNotice from "./UnpaidNotice";
import RenterInfo from "../EditBooking/RenterInfo";
import ConnectedBookings from "../ConnectedBookings/ConnectedBookings";
import { getAgreementPdfBase } from "utils/agreement-url";
import DetailPagetLayout from "./Layout";
import {
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import Cancellation from "../CheckOutPage/Cancellation";
import Invoice from "../(car-search-form)/invoice";
import { storageGet, storageSet } from "utils/safeStorage";
import { specText } from "utils/carSpecs";

export interface bookingDetailPageProps {
  className?: string;
}

const VIDEO_EXTS = [".mp4", ".mkv", ".wmv", ".mov", ".webm", ".avi", ".flv", ".m4v"];

/* 1NOW-1663 — ID / insurance verification states.
   Pending is a next step on an already-paid booking, so it gets a quiet amber
   rather than the error red it used to share with destructive actions. Red is
   now reserved for genuinely destructive controls (the file delete buttons). */
const VERIFIED_CLS = "text-green-600";
const PENDING_CLS = "text-amber-600 pending-attention";
/* Copying a verification link is a neutral action — it was styled as an alarm. */
const COPY_LINK_CLS = "text-text-color underline underline-offset-2 hover:opacity-75";

/** Pending label. The amber and the breathing loop come from PENDING_CLS on the
    parent; this only keeps the label off a second line, since the label beside
    it ("Insurance Verification") already wraps. */
const PendingChip: FC<{ label: string }> = ({ label }) => (
  <span className="whitespace-nowrap">{label}</span>
);

function classifyTripPhotos(photos: any[], type: string) {
  let images = 0;
  let videos = 0;
  for (const p of photos) {
    if (p?.image_type !== type) continue;
    const name = String(p?.photo_name || p?.image || "").toLowerCase();
    if (VIDEO_EXTS.some((ext) => name.endsWith(ext))) videos += 1;
    else images += 1;
  }
  return { images, videos };
}

function summarizeFileMix(b: { images: number; videos: number }) {
  if (!b.images && !b.videos) return "No files yet";
  const imgPart = b.images === 1 ? "1 image" : `${b.images} images`;
  const vidPart = b.videos === 1 ? "1 video" : `${b.videos} videos`;
  if (b.videos === 0) return imgPart;
  if (b.images === 0) return vidPart;
  return `${imgPart}, ${vidPart}`;
}

const BookingDetailPage: FC<bookingDetailPageProps> = ({ className = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    ourCompany,
    setPickupLocation,
    setSelectedExtras,
    setSelectedBonzahProducts,
    selectedCar,
    setSearchedDropoffLocation,
    setSearchedPickupLocation,
    setSelectedCar,
    setDropoffLocation,
    setEndDate,
    setStartDate,
    setDropoffLocationType,
    setStartTime,
    startTime,
    setEndTime,
    setEndTimeTouched,
    holdSavedTrip,
    endTime,
  } = useData();

  const queryParams = new URLSearchParams(location.search);
  let bookingId = queryParams.get("id");

  const [bookingloading, setBookingLoading] = useState(true);
  const [isTripInspectionOpen, setIsTripInspectionOpen] = useState(false);
  const [isTotalBillModalOpen, setIsTotalBillModalOpen] = useState(false);
  let [isOpen, setIsOpen] = useState(false);
  let [bookingDetails, setBookingDetail] = useState<any | null>(null);
  const [canopyLoading, setCanopyLoading] = useState(false);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [drivercanopyLoading, setDriverCanopyLoading] = useState(false);
  const [driveridentityLoading, setDriverIdentityLoading] = useState(false);
  const [canopyLink, setCanopyLink] = useState<any>();
  const [identityLink, setIdentityLink] = useState<any>();
  const [drivercanopyLink, setDriverCanopyLink] = useState<any>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [driveridentityLink, setDriverIdentityLink] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [isRenterInfoEdit, setIsRenterInfoEdit] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [showImgGallery, setShowImgGallery] = useState(false);
  const [isCancellationPolicyModalOpen, setIsCancellationPolicyModalOpen] =
    useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancellationPolicyChecked, setCancellationPolicyChecked] =
    useState(false);
  const [isCancellationOpen, setIsCancellationOpen] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState();
  const [cancellationPolicy, setCancellationPolicy] = useState();
  const [loading, setLoading] = useState<boolean>(true);
  const [showCopyIdentity, setShowCopyIdentity] = useState(false);
  const [showCopyCanopy, setShowCopyCanopy] = useState(false);
  const [showCopyDriverIdentity, setShowCopyDriverIdentity] = useState(false);
  const [showCopyDriverCanopy, setShowCopyDriverCanopy] = useState(false);
  const [isGridOpen, setIsGridOpen] = useState(false);
  const [insuranceUploading, setInsuranceUploading] = useState(false);
  const [insuranceUploadProgress, setInsuranceUploadProgress] = useState(0);
  const [pendingInsuranceFiles, setPendingInsuranceFiles] = useState<File[]>([]);
  const COPY_TOAST_ID = "copy_link";

  // Insurance documents state
  const [insuranceDocuments, setInsuranceDocuments] = useState<any[]>([]);

  // ID documents state
  const [idDocuments, setIdDocuments] = useState<any[]>([]);
  const [pendingIdFiles, setPendingIdFiles] = useState<File[]>([]);
  const [idUploading, setIdUploading] = useState(false);
  const [idUploadProgress, setIdUploadProgress] = useState(0);

  // Odometer reading state
  const [preOdometerImage, setPreOdometerImage] = useState<string | null>(null);
  const [postOdometerImage, setPostOdometerImage] = useState<string | null>(null);
  const [preOdometerLoading, setPreOdometerLoading] = useState(false);
  const [postOdometerLoading, setPostOdometerLoading] = useState(false);
  const [preOdometerResult, setPreOdometerResult] =
    useState<OdometerReadResult | null>(null);
  const [postOdometerResult, setPostOdometerResult] =
    useState<OdometerReadResult | null>(null);
  const [preOdometerError, setPreOdometerError] = useState<string | null>(null);
  const [postOdometerError, setPostOdometerError] = useState<string | null>(null);
  const [preTripBreakdown, setPreTripBreakdown] = useState({
    images: 0,
    videos: 0,
  });
  const [postTripBreakdown, setPostTripBreakdown] = useState({
    images: 0,
    videos: 0,
  });
  const [tripInspectionInitialPhase, setTripInspectionInitialPhase] = useState<
    "pre" | "post"
  >("pre");

  /* 1NOW-10707 — every request that names a booking or a renter sends the
     uuid, never the sequential pk. These endpoints take no login, so a
     guessable integer let anyone read a stranger's booking or start
     verification against their identity. Both uuids are already here: the page
     was opened with the booking's, and the API returns the customer's. */
  const bookingUid: string | undefined =
    bookingDetails?.unique_identifier ?? bookingId ?? undefined;
  const customerUid: string | undefined =
    bookingDetails?.customer?.unique_identifier;
  const secondaryDriverUid: string | undefined =
    bookingDetails?.secondary_driver_obj?.unique_identifier;

  const handleOdometerUpload = async (file: File, isPre: boolean) => {
    if (!bookingUid) return;
    const setLoading = isPre ? setPreOdometerLoading : setPostOdometerLoading;
    const setResult = isPre ? setPreOdometerResult : setPostOdometerResult;
    const setError = isPre ? setPreOdometerError : setPostOdometerError;
    const setImage = isPre ? setPreOdometerImage : setPostOdometerImage;

    setLoading(true);
    setError(null);
    setResult(null);
    setImage(URL.createObjectURL(file));
    try {
      const { data, error } = await readOdometer(
        file,
        bookingUid,
        isPre ? "pre_odometer" : "post_odometer",
      );
      if (error || !data) {
        setError(error || "Could not read the odometer.");
      } else {
        setResult(data);
        setImage(data.image_url);
      }
    } finally {
      setLoading(false);
    }
  };

  const onDropInsurance = useCallback((acceptedFiles: File[]) => {
    setPendingInsuranceFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps: getInsuranceRootProps, getInputProps: getInsuranceInputProps, isDragActive: isInsuranceDragActive } = useDropzone({
    onDrop: onDropInsurance,
    accept: { "image/*": [], "application/pdf": [] },
    multiple: true,
  });

  const onDropId = useCallback((acceptedFiles: File[]) => {
    setPendingIdFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps: getIdRootProps, getInputProps: getIdInputProps, isDragActive: isIdDragActive } = useDropzone({
    onDrop: onDropId,
    accept: { "image/*": [], "application/pdf": [] },
    multiple: true,
  });

  const handleInsuranceUpload = async () => {
    if (!customerUid || pendingInsuranceFiles.length === 0) return;
    setInsuranceUploading(true);
    setInsuranceUploadProgress(0);
    try {
      const result = await uploadCustomerFiles(
        customerUid,
        pendingInsuranceFiles,
        "insurance",
        (percent) => setInsuranceUploadProgress(percent)
      );
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setInsuranceUploadProgress(100);
      setPendingInsuranceFiles([]);
      toast.success("Documents uploaded successfully");
      fetchInsuranceDocuments(customerUid);
    } catch {
      toast.error("Upload failed");
    } finally {
      setInsuranceUploading(false);
      setInsuranceUploadProgress(0);
    }
  };

  const fetchInsuranceDocuments = async (customerId: number | string) => {
    const result = await getCustomerFiles(customerId, "insurance");
    if (result.data) {
      setInsuranceDocuments(Array.isArray(result.data) ? result.data : result.data.results ?? []);
    }
  };

  const fetchIdDocuments = async (customerId: number | string) => {
    const result = await getCustomerFiles(customerId, "identity");
    if (result.data) {
      setIdDocuments(Array.isArray(result.data) ? result.data : result.data.results ?? []);
    }
  };

  const handleIdUpload = async () => {
    if (!customerUid || pendingIdFiles.length === 0) return;
    setIdUploading(true);
    setIdUploadProgress(0);
    try {
      const result = await uploadCustomerFiles(
        customerUid,
        pendingIdFiles,
        "identity",
        (percent) => setIdUploadProgress(percent)
      );
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setIdUploadProgress(100);
      setPendingIdFiles([]);
      toast.success("ID documents uploaded successfully");
      fetchIdDocuments(customerUid);
    } catch {
      toast.error("Upload failed");
    } finally {
      setIdUploading(false);
      setIdUploadProgress(0);
    }
  };

  const handleOpenModalImageGallery = (index: number) => {
    if (bookingDetails?.fleet?.fleet_photos?.length === 0) {
      toast("No Images available for this fleet", {
        style: {
          background: "#fff3cd",
          color: "#856404",
        },
      });
    } else {
      setCurrentIndex(index);
      setIsOpenModal(true);
    }
  };

  const closeModal = () => {
    setIsOpenModal(false);
  };

  const handleCloseEdit = () => {
    setIsEdit(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const modifyBooking = async (bookingId: string) => {
    navigate(`/modify-booking/${bookingId}`);
    return;
  };

  const handleCancelBooking = async () => {
    if (!bookingId) return;
    if (cancellationPolicyChecked !== true) {
      toast.error("Please accept our Cancellation Policy");
      return;
    }
    setIsCancelling(true);
    try {
      const result = await cancelBooking(bookingId);

      if (result.error) {
        toast.error(result.error);
      } else {
        // 1NOW-1171: explicit success confirmation; the UI refreshes via
        // fetchBookingDetail() below (no manual reload). A pending request
        // (approval required) comes back as a {detail} message; a direct
        // cancel returns the cancelled booking.
        toast.success(
          result?.data?.detail
            ? "Cancellation request submitted for approval."
            : "Booking cancelled.",
        );
        setIsCancelModalOpen(false);
        setIsEdit(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      toast.error("Couldn't cancel booking. Please try again.");
    } finally {
      setIsCancelling(false);

      fetchBookingDetail();
    }
  };

  const handleOpenTotalBillModal = () => {
    setIsTotalBillModalOpen(true);
  };

  const handleCloseTotalBillModal = () => {
    setIsTotalBillModalOpen(false);
  };

  function close() {
    setIsOpen(false);
  }
  const closeGridModal = () => setIsGridOpen(false);

  useEffect(() => {
    const getContent = async () => {
      setLoading(true);
      try {
        // readPolicyContent: a failed request is not "the operator has
        // written nothing", and a missing type is simply none.
        const content = readPolicyContent(await getPrivacyPolicyContent());
        setCancellationPolicy(policySections(content, "cancellation_policy") as any);
        setPrivacyPolicy(policySections(content, "privacy_policy") as any);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching content", error);
      }
    };

    getContent();
  }, []);

  /* The agreement key is the booking's uuid. It used to be
     `{pk}b6cr4{companyId}b6cr4{base64(email)}`, but the backend only ever read
     the first segment — the company id was logged and never checked, and the
     email was ignored outright — so a guessed integer opened any operator's
     agreement (1NOW-10707). Both dead segments are gone with it. */
  const openRentalAgreement = async () => {
    if (!bookingUid) return;
    const url = getAgreementPdfBase();
    window.open(`${url}${encodeURIComponent(bookingUid)}`, "_blank");
  };

  function extractSubstringInBrackets(input: string) {
    if (typeof input !== "string") {
      throw new Error("Input must be a string.");
    }
    const match = input.match(/\(([^)]+)\)/);
    return match ? match[1] : null;
  }

  const sendAgreementEmail = async (type: string) => {
    if (!bookingUid) return;
    try {
      const data: any = await sendRentalAgreement(bookingUid, type);
      // sendRentalAgreement answers { error } rather than throwing, so this
      // said "Email successfully sent!" for every failed send.
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success("Email successfully sent!");
    } catch (error: any) {
      toast.error(renterError(error, "booking"));
    }
  };

  /**
   * What the booking bills, as the backend charges it: the invoice's
   * after-coupon trip total, else the booking's own figure — in dollars. The
   * two copies printed the raw string, one without its "$", and the other
   * dropped the "$" exactly when a coupon was applied.
   */
  const billed =
    chargedNow(bookingDetails?.invoice) ??
    (bookingDetails?.final_price_without_security != null
      ? Number(bookingDetails.final_price_without_security)
      : null);
  const totalBill = billed != null && Number.isFinite(billed) ? cash(billed) : "—";
  // 1NOW-11476: a pay-later reservation not yet paid shows its details (trip, invoice, agreement, renter) but none of
  // the actions: no ID / insurance verification or uploads, trip photos, renter edit, Edit / Cancel Booking, or other
  // bookings, and a Pay and book notice on top (UnpaidNotice). All of it comes back once it is paid.
  const unpaidReservation = reservationUnpaid(bookingDetails);

  const fetchBookingDetail = async (prefetchedData?: any) => {
    if (prefetchedData) {
      setBookingDetail(prefetchedData);
      return;
    }
    setBookingLoading(true);
    if (!bookingId) {
      toast.error("Booking ID is missing");
      setBookingLoading(false);
      return;
    }
    try {
      const data = await retriveBooking(bookingId);
      // isBooking: an answer without a booking in it (a proxy's HTML page, a
      // 200 with no id) is not stored as the booking.
      if (!isBooking(data)) {
        throw new Error((data as any)?.error ?? "Booking not found");
      }
      setBookingDetail(data);
      setBookingLoading(false);
    } catch (error) {
      toast.error("Booking Not Found");
      setBookingDetail(null);
      setBookingLoading(false);
    }
  };
  useEffect(() => {
    fetchBookingDetail();
  }, [bookingId]);

  useEffect(() => {
    if (customerUid) {
      fetchIdDocuments(customerUid);
      fetchInsuranceDocuments(customerUid);
    }
  }, [customerUid]);

  useEffect(() => {
    if (!bookingUid) return;
    let cancelled = false;
    (async () => {
      const result = await getTripPhotos(bookingUid);
      if (cancelled) return;
      const photos: any[] = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
          ? result.data
          : [];
      const latest = (type: string) =>
        photos
          .filter((p) => p.image_type === type)
          .sort((a, b) =>
            new Date(b.created_at || 0).getTime() -
            new Date(a.created_at || 0).getTime(),
          )[0];
      const preImg = latest("pre_odometer");
      const postImg = latest("post_odometer");
      if (preImg?.image) setPreOdometerImage(preImg.image);
      if (postImg?.image) setPostOdometerImage(postImg.image);
      setPreTripBreakdown(classifyTripPhotos(photos, "pre_trip"));
      setPostTripBreakdown(classifyTripPhotos(photos, "post_trip"));
      // The booking may not have loaded (a bad link): this ran regardless and
      // threw on null.
      if (bookingDetails?.miles_before != null) {
        setPreOdometerResult({
          miles: Number(bookingDetails?.miles_before),
          raw_reading: Number(bookingDetails?.miles_before),
          unit: "mi",
          image_id: preImg?.id ?? 0,
          image_url: preImg?.image ?? "",
          image_type: "pre_odometer",
        });
      }
      if (bookingDetails?.miles_after != null) {
        setPostOdometerResult({
          miles: Number(bookingDetails?.miles_after),
          raw_reading: Number(bookingDetails?.miles_after),
          unit: "mi",
          image_id: postImg?.id ?? 0,
          image_url: postImg?.image ?? "",
          image_type: "post_odometer",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingDetails?.id, bookingDetails?.miles_before, bookingDetails?.miles_after]);

  useEffect(() => {
    if (bookingDetails && bookingDetails.trip_status !== "cancelled") {
      const modalShownKey = `cancellationPolicyShown_${bookingDetails.id}`;
      const hasBeenShown = storageGet("local", modalShownKey);

      if (
        !hasBeenShown &&
        (!bookingDetails.customer?.identity_verified ||
          !bookingDetails.customer?.insurance_verified)
      ) {
        setIsCancellationPolicyModalOpen(true);
        storageSet("local", modalShownKey, "true");
      }
    }
  }, [bookingDetails]);

  // This page pre-fills the search from the saved booking below, so the pick-up time it loads is
  // the booking's own and is never snapped to the location's slots (1NOW-11108). Declared before
  // the pre-fill so the hold is up when the provider reads the new state.
  useEffect(() => holdSavedTrip(), [holdSavedTrip]);

  useEffect(() => {
    if (bookingDetails) {
      setDropoffLocationType(bookingDetails?.same_drop_off);
      setPickupLocation(bookingDetails?.pick_up_location);
      setSearchedPickupLocation(bookingDetails?.custom_pick_up_address);
      setSearchedDropoffLocation(bookingDetails?.custom_drop_off_address);
      setDropoffLocation(bookingDetails?.drop_off_location);
      setStartTime(
        formatTimeToHumanFirendly24Hour(bookingDetails?.pick_up_time),
      );
      setEndTimeTouched(true);
      setEndTime(
        formatTimeToHumanFirendly24Hour(bookingDetails?.drop_off_time),
      );
      setStartDate(
        new Date(formatDateToHumanFirendly(bookingDetails?.pick_up_time)),
      );
      setEndDate(
        new Date(formatDateToHumanFirendly(bookingDetails?.drop_off_time)),
      );
      setSelectedCar(bookingDetails?.fleet);
      if (bookingDetails?.extras?.length > 0) {
        const extraIds = new Set<number>(
          bookingDetails.extras.map((extra: any) => extra.id),
        );
        setSelectedExtras(extraIds);
      }

      if (bookingDetails?.bonzah_insurance_options?.length > 0) {
        const insuranceIds = bookingDetails?.bonzah_insurance_options?.map(
          (insurance: any) => insurance,
        );
        setSelectedBonzahProducts(insuranceIds);
      }
    }
  }, [isEdit]);

  // Handle browser back button to navigate to home
  // 1NOW-11157 (Faiza, 8 Oct): not when the renter opened this booking from Track my booking's list. Back went
  // home and the list was gone, behind a new code. The list's link says so in router state (never in the URL).
  const fromTrackBooking = (location.state as { fromTrackBooking?: boolean } | null)?.fromTrackBooking === true;
  // 1NOW-11501: the booking's page, where a reservation or a payment lands, is the end of the funnel: counted once per
  // booking (its number, never its uuid), not for a renter who opened it from Track my booking's list.
  const viewedBooking = useRef<unknown>(null);
  useEffect(() => {
    const id = bookingDetails?.id;
    if (fromTrackBooking || id === undefined || viewedBooking.current === id) return;
    viewedBooking.current = id;
    trackBookingStep("booking_page", { booking_id: id });
  });
  useEffect(() => {
    if (fromTrackBooking) return;
    // Push a new state when component mounts, preserving query params
    window.history.pushState(
      null,
      "",
      window.location.pathname + window.location.search,
    );

    const handlePopState = (event: PopStateEvent) => {
      // Prevent default back behavior and navigate to home
      window.history.pushState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
      navigate("/");
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate, fromTrackBooking]);

  const handleCanopyVerificationButtonClick = async () => {
    // Drop the click if a previous request hasn't resolved — otherwise
    // rapid clicks fire N requests and stack N duplicate error toasts
    // (ONENOW-466). Passing a stable `id` to `toast.error` also collapses
    // any duplicates that do slip through into a single toast.
    if (canopyLoading) return;
    setCanopyLoading(true);
    const payload: CanopyPayload = {
      user_id: customerUid ?? "",
    };
    const IgnitionResponse = (await CreateCanopyVerification(payload)) as {
      data?: { insurance_verification_link?: string };
      error?: string;
    };
    if (
      IgnitionResponse.data &&
      IgnitionResponse.data.insurance_verification_link
    ) {
      const ignitionUrl = IgnitionResponse.data.insurance_verification_link;
      setCanopyLink(ignitionUrl);
      setCanopyLoading(false);
      return;
    } else {
      console.error(
        "Failed to retrieve insurance verification link:",
        IgnitionResponse.error,
      );
      toast.error("Verification failed. Please try again later.", {
        id: "canopy-verification-error",
      });
      setCanopyLoading(false);
    }
  };

  const handlePrimaryDriverVerificationButtonClick = async () => {
    if (identityLoading) return; // ONENOW-466 — drop rapid clicks
    setIdentityLoading(true);
    const payload = { customer_id: customerUid ?? "" };
    if (ourCompany?.default_identity === "vouched") {
      const vouchSessionResponse = (await CreateVouchVerification(payload)) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (vouchSessionResponse.data && vouchSessionResponse.data.session_url) {
        const sessionUrl = vouchSessionResponse.data.session_url;
        setIdentityLink(sessionUrl);
        setIdentityLoading(false);
        return;
      } else {
        console.error(
          "Failed to retrieve session URL:",
          vouchSessionResponse.error,
        );
        toast.error(
          vouchSessionResponse?.error ??
          "Verification failed. Please try again later.",
          { id: "identity-verification-error" },
        );
        setIdentityLoading(false);
      }
    } else {
      const stripeSessionResponse = (await CreateStripeVerification(
        payload,
      )) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (
        stripeSessionResponse.data &&
        stripeSessionResponse.data.session_url
      ) {
        const sessionUrl = stripeSessionResponse.data.session_url;
        setIdentityLink(sessionUrl);
        setIdentityLoading(false);
        return;
      } else {
        console.error(
          "Failed to retrieve session URL:",
          stripeSessionResponse.error,
        );
        toast.error(
          stripeSessionResponse?.error ??
          "Verification failed. Please try again later.",
          { id: "identity-verification-error" },
        );
        setIdentityLoading(false);
      }
    }
  };
  const handleDriverCanopyVerificationButtonClick = async () => {
    if (drivercanopyLoading) return; // ONENOW-466 — drop rapid clicks
    setDriverCanopyLoading(true);
    const payload: CanopyPayload = {
      user_id: secondaryDriverUid ?? "",
    };
    const IgnitionResponse = (await CreateCanopyVerification(payload)) as {
      data?: { insurance_verification_link?: string };
      error?: string;
    };
    if (
      IgnitionResponse.data &&
      IgnitionResponse.data.insurance_verification_link
    ) {
      const ignitionUrl = IgnitionResponse.data.insurance_verification_link;
      setDriverCanopyLink(ignitionUrl);
      setDriverCanopyLoading(false);
      return;
    } else {
      console.error(
        "Failed to retrieve insurance verification link:",
        IgnitionResponse.error,
      );
      toast.error("Verification failed. Please try again later.", {
        id: "driver-canopy-verification-error",
      });
      setDriverCanopyLoading(false);
    }
  };

  const handleDriverPrimaryDriverVerificationButtonClick = async () => {
    if (driveridentityLoading) return; // ONENOW-466 — drop rapid clicks
    setDriverIdentityLoading(true);
    const payload = {
      customer_id: secondaryDriverUid ?? "",
    };
    let sessionUrl = "";
    if (ourCompany?.default_identity === "vouched") {
      const vouchSessionResponse = (await CreateVouchVerification(payload)) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (vouchSessionResponse.data && vouchSessionResponse.data.session_url) {
        sessionUrl = vouchSessionResponse.data.session_url;
        setDriverIdentityLink(sessionUrl);
        setDriverIdentityLoading(false);
        return;
      } else {
        console.error(
          "Failed to retrieve session URL:",
          vouchSessionResponse.error,
        );
        toast.error("Verification failed. Please try again later.", {
          id: "driver-identity-verification-error",
        });
        setDriverIdentityLoading(false);
      }
    } else {
      const stripeSessionResponse = (await CreateStripeVerification(
        payload,
      )) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (
        stripeSessionResponse.data &&
        stripeSessionResponse.data.session_url
      ) {
        sessionUrl = stripeSessionResponse.data.session_url;
        setDriverIdentityLink(sessionUrl);
        setDriverIdentityLoading(false);
        return;
      } else {
        console.error(
          "Failed to retrieve session URL:",
          stripeSessionResponse.error,
        );
        toast.error(
          stripeSessionResponse.error ??
          "Verification failed. Please try again later.",
          { id: "driver-identity-verification-error" },
        );
        setDriverIdentityLoading(false);
      }
    }
    setDriverIdentityLink(sessionUrl);
    setDriverIdentityLoading(false);
    // Try to open in new tab
    const newTab = window.open(sessionUrl, "_blank");
    if (!newTab) {
      setShowCopyDriverIdentity(true);
      toast("Popup blocked. Please copy the link manually.", {
        style: { background: "#fff3cd", color: "#856404" },
        id: "driver-identity-popup-blocked",
      });
    }
  };

  const renderImgSection = () => {
    return (
      <div>
        <header className="rounded-md sm:rounded-xl">
          <div className="relative grid grid-cols-1 md:grid-cols-4  gap-1 sm:gap-2 min-h-96 ">
            <div
              className="col-span-2 row-span-2 relative rounded-md sm:rounded-xl overflow-hidden cursor-pointer"
              onClick={() => {
                handleOpenModalImageGallery(0);
                setCurrentIndex(0);
              }}
            >
              <img
                src={
                  photoUrl(bookingDetails?.fleet?.fleet_photos?.[0]?.photo_url) ??
                  "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
                }
                alt="0"
                className="absolute inset-0 object-cover rounded-md sm:rounded-xl w-full h-full"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
              />
              <div className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity"></div>
            </div>

            {/*  */}
            <div
              className="col-span-1 row-span-2 relative rounded-md sm:rounded-xl overflow-hidden cursor-pointer"
              onClick={() => {
                handleOpenModalImageGallery(1);
                setCurrentIndex(1);
              }}
            >
              <img
                className="absolute inset-0 object-cover rounded-md sm:rounded-xl w-full h-full"
                src={
                  photoUrl(bookingDetails?.fleet?.fleet_photos?.[1]?.photo_url) ??
                  "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
                }
                alt="1"
                sizes="400px"
              />
              <div className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity"></div>
            </div>

            {/*  */}
            {bookingDetails?.fleet?.fleet_photos
              ?.filter((_: any, i: any) => i >= 2 && i < 4)
              .map((item: any, index: any) => (
                <div
                  key={index}
                  className={`relative rounded-md sm:rounded-xl overflow-hidden ${index >= 2 ? "block" : ""
                    }`}
                >
                  <div className="aspect-w-4 aspect-h-3">
                    <img
                      className="absolute inset-0 object-cover w-full h-full rounded-md sm:rounded-xl "
                      src={
                        photoUrl(item.photo_url) ?? ""
                      }
                      alt="photos"
                      sizes="400px"
                    />
                  </div>
                  <div
                    className="absolute inset-0 bg-neutral-900 bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={() => {
                      handleOpenModalImageGallery(2);
                      setCurrentIndex(2);
                    }}
                  />
                </div>
              ))}

            {((bookingDetails?.fleet?.fleet_photos?.length ?? 0) > 1) && (
              <div
                className="absolute flex md:items-center md:justify-center left-3 bottom-3 px-4 py-2 rounded-xl bg-neutral-100 text-neutral-500 cursor-pointer hover:bg-neutral-200 z-10"
                onClick={() => setIsGridOpen(true)}
              >
                <Squares2X2Icon className="h-5 w-5" />
                <span className="ml-2 text-neutral-800 text-sm font-medium">
                  Show all photos
                </span>
              </div>
            )}
          </div>
        </header>

        {isGridOpen && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4">
            <div className="relative w-full max-w-5xl bg-white rounded-lg overflow-auto p-4">
              <button
                className="absolute top-1 right-3 text-lg text-text-color bg-white rounded-full w-10 h-10 flex items-center justify-center z-10"
                onClick={closeGridModal}
                aria-label="Close gallery"
                type="button"
              >
                ✖
              </button>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 pt-8">
                {(bookingDetails?.fleet?.fleet_photos ?? []).map(
                  (item: any, idx: number) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setCurrentIndex(idx);
                        setIsOpenModal(true);
                        setIsGridOpen(false);
                      }}
                      className="w-full h-32 sm:h-40 md:h-48 overflow-hidden rounded-md focus:outline-none focus:ring-2 focus:ring-primary-brand transition-transform hover:scale-105"
                      type="button"
                    >
                      <img
                        src={
                          photoUrl(item.photo_url) ?? ""
                        }
                        alt={`photo-${idx}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ),
                )}
                {(bookingDetails?.fleet?.fleet_photos?.length ?? 0) === 0 && (
                  <div className="col-span-full text-center p-8 text-text-color">
                    No images available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {isOpenModal && (
          <div className="fixed h-full top-0 left-0 w-full  bg-black bg-opacity-90 flex items-center justify-center z-50">
            <button
              className="absolute top-5 right-5 text-white text-xl"
              onClick={closeModal}
            >
              ✖
            </button>

            <div className="w-4/5 ">
              <Slide
                defaultIndex={currentIndex}
                indicators={
                  (bookingDetails?.fleet?.fleet_photos?.length ?? 0) > 1
                }
                arrows={(bookingDetails?.fleet?.fleet_photos?.length ?? 0) > 1}
                autoplay={false}
                duration={2000}
                transitionDuration={300}
                onChange={(current, next) => {
                  setCurrentIndex(next);
                }}
                prevArrow={
                  <button
                    className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-transparent text-text-color ${(bookingDetails?.fleet?.fleet_photos?.length ?? 0) <= 1 ||
                      currentIndex === 0
                      ? "invisible"
                      : ""
                      }`}
                    type="button"
                    aria-label="Previous"
                  >
                    <span className="default-nav !text-text-color">❮</span>
                  </button>
                }
                nextArrow={
                  <button
                    className={`absolute right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-transparent text-text-color ${(bookingDetails?.fleet?.fleet_photos?.length ?? 0) <= 1 ||
                      currentIndex ===
                      (bookingDetails?.fleet?.fleet_photos?.length ?? 0) - 1
                      ? "invisible"
                      : ""
                      }`}
                    type="button"
                    aria-label="Next"
                  >
                    <span className="default-nav !text-text-color">❯</span>
                  </button>
                }
              >
                {(bookingDetails?.fleet?.fleet_photos?.length || 0) > 0 ? (
                  bookingDetails?.fleet?.fleet_photos?.map(
                    (item: any, index: number) => (
                      <div
                        className="flex justify-center items-center h-full"
                        key={index}
                      >
                        <img
                          className="w-full max-h-full object-contain rounded-lg"
                          src={
                            photoUrl(item.photo_url) ?? ""
                          }
                          alt={`Slide ${index}`}
                        />
                      </div>
                    ),
                  )
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <img
                      className="w-full max-h-full object-contain rounded-lg"
                      src="https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
                      alt="No images available"
                    />
                  </div>
                )}
              </Slide>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBookingDetailsSection = () => {
    return (
      <>
        <div className="mt-8">
          <div className="grid grid-cols-2 ">
            <div className="col-span-1">
              <span className=" text-lg text-text-color">Pick Up</span>
              <br />
              <span className="mt-1.5 text-lg font-bold text-text-color">
                <span>
                  {formatDateToHumanFirendly(bookingDetails?.pick_up_time)}
                </span>
                <br />
                <span className="font-normal text-text-color">
                  {uses24Hour(ourCompany)
                    ? formatTimeToHumanFirendly24Hour(
                      bookingDetails?.pick_up_time,
                    )
                    : formatTimeToHumanFirendly12Hour(
                      bookingDetails?.pick_up_time,
                    )}
                </span>
              </span>
              <br />
              <span className="mt-1.5 text-lg text-text-color ">
                {bookingDetails?.custom_pick_up_address === ""
                  ? bookingDetails?.pick_up_location?.name
                  : bookingDetails?.custom_pick_up_address}
              </span>
            </div>
            <div className="gap-2">
              <span className=" text-lg text-text-color">Drop Off</span>
              <br />
              <span className="mt-1.5 text-lg font-bold  text-text-color">
                <span>
                  {formatDateToHumanFirendly(bookingDetails?.drop_off_time)}
                </span>
                <br />
                <span className="font-normal text-text-color">
                  {uses24Hour(ourCompany)
                    ? formatTimeToHumanFirendly24Hour(
                      bookingDetails?.drop_off_time,
                    )
                    : formatTimeToHumanFirendly12Hour(
                      bookingDetails?.drop_off_time,
                    )}
                </span>
              </span>
              <br />
              <span className="mt-1.5 text-lg text-text-color">
                {bookingDetails?.custom_drop_off_address === ""
                  ? bookingDetails?.drop_off_location?.name
                  : bookingDetails?.custom_drop_off_address
                    ? bookingDetails?.custom_drop_off_address
                    : bookingDetails?.custom_pick_up_address}
              </span>
            </div>
          </div>
          {!unpaidReservation && bookingDetails?.trip_status !== "cancelled" && (
            <div className="mt-5 flex w-full flex-col gap-4 rounded-lg border border-secondary-100 bg-bg-color p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-lg font-bold text-text-color">
                    Upload Trip Photos
                  </div>
                  <div className="text-sm text-text-color/70">
                    Pre-trip and post-trip vehicle photos with odometer
                    captures.
                  </div>
                </div>
                <button
                  onClick={() => {
                    setTripInspectionInitialPhase("pre");
                    setIsTripInspectionOpen(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-brand px-4 py-2.5 text-sm font-semibold text-on-primary transition-opacity hover:opacity-90"
                >
                  <CameraIcon className="h-4 w-4" />
                  Upload Trip Photos
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    setTripInspectionInitialPhase("pre");
                    setIsTripInspectionOpen(true);
                  }}
                  className="flex items-center justify-between rounded-lg border border-secondary-100 bg-secondary-50 px-4 py-3 text-left transition hover:border-primary-brand"
                >
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-color/60">
                      Pre-trip
                    </div>
                    <div className="mt-1 text-sm font-semibold text-text-color">
                      {summarizeFileMix(preTripBreakdown)}
                    </div>
                    <div className="text-xs text-text-color/70">
                      {!preOdometerImage
                        ? "0 mi"
                        : bookingDetails?.miles_before != null
                          ? `${Number(bookingDetails.miles_before).toLocaleString()} mi`
                          : "Mileage not set"}
                    </div>
                  </div>
                  <svg
                    className="h-4 w-4 -rotate-45 text-text-color/60"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setTripInspectionInitialPhase("post");
                    setIsTripInspectionOpen(true);
                  }}
                  className="flex items-center justify-between rounded-lg border border-secondary-100 bg-secondary-50 px-4 py-3 text-left transition hover:border-primary-brand"
                >
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-color/60">
                      Post-trip
                    </div>
                    <div className="mt-1 text-sm font-semibold text-text-color">
                      {summarizeFileMix(postTripBreakdown)}
                    </div>
                    <div className="text-xs text-text-color/70">
                      {!postOdometerImage
                        ? "0 mi"
                        : bookingDetails?.miles_after != null
                          ? `${Number(bookingDetails.miles_after).toLocaleString()} mi`
                          : "Mileage not set"}
                    </div>
                  </div>
                  <svg
                    className="h-4 w-4 -rotate-45 text-text-color/60"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-col lg:flex-row lg:justify-between gap-0 md:gap-12">
            <div className="py-8 w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className=" mb-6 md:mb-0">
                  <h2 className="text-lg font-bold mb-8 text-text-color">
                    Trip Info
                  </h2>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-text-color ">Total Bill</p>
                      <p className="text-text-color ">{totalBill}</p>
                    </div>

                    <div
                      onClick={handleOpenTotalBillModal}
                      className="text-text-color cursor-pointer mx-2"
                    >
                      <div className="flex text-text-color font-semibold">
                        View Invoice
                        <svg
                          width="23"
                          height="26"
                          viewBox="0 0 23 26"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                            fill="#90A3BF"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-text-color">Agreement</p>
                    </div>
                    <div
                      onClick={openRentalAgreement}
                      className={`cursor-pointer mx-2 ${COPY_LINK_CLS}`}
                    >
                      <div className="flex font-semibold">
                        View Agreement
                        <svg
                          width="23"
                          height="26"
                          viewBox="0 0 23 26"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                            fill="#90A3BF"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-text-color ">Get Booking Email</p>
                    </div>
                    <span
                      className="text-text-color mx-2 cursor-pointer font-semibold"
                      onClick={() => sendAgreementEmail("booking_confirmation")}
                    >
                      Resend email
                    </span>
                  </div>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-text-color ">Get Agreement</p>
                    </div>
                    <span
                      className="text-text-color mx-2 cursor-pointer font-semibold"
                      onClick={() => sendAgreementEmail("rental_agreement")}
                    >
                      Resend email
                    </span>
                  </div>

                  {bookingDetails?.bonzah_purchase_response_data?.data?.coverage_information
                    ?.filter((insurance: any) => insurance?.added === true)
                    ?.map((insurance: any, index: any) => (
                      <div
                        key={index}
                        onClick={async () => {
                          try {
                            const { data, error }: any = await getBonzahPDF(
                              bookingUid,
                              extractSubstringInBrackets(
                                insurance?.optional_addon_cover_name?.toLowerCase() ??
                                "",
                              ) ?? "",
                            );

                            if (error) {
                              throw new Error(error);
                            }
                            if (data instanceof ArrayBuffer) {
                              const pdfBlob = new Blob([data], {
                                type: "application/pdf",
                              });

                              const pdfUrl = URL.createObjectURL(pdfBlob);
                              const newTab = window.open(pdfUrl, "_blank");

                              if (newTab) {
                                newTab.focus();
                              } else {
                                const pdfUrl = URL.createObjectURL(pdfBlob);
                                const link = document.createElement("a");
                                link.href = pdfUrl;
                                link.target = "_blank";
                                link.download = "insurance_agreement.pdf";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }
                            } else {
                              throw new Error("Invalid PDF data received.");
                            }
                          } catch (error: any) {
                            console.error("Error fetching PDF:", error);
                            toast.error(
                              error.message ||
                              "Couldn't open document. Please try again.",
                            );
                          }
                        }}
                        className="w-full cursor-pointer justify-between overflow-clip rounded-md"
                      >
                        <div className="mt-3 flex items-center justify-between">
                          <div>
                            <div className="text-text-color ">
                              Insurance Agreement:{" "}
                              {insurance?.optional_addon_cover_name}
                            </div>
                            <div className="text-xs font-medium text-text-color">
                              {insurance?.policy_created_date}
                            </div>
                          </div>
                          <div className="min-h-5 min-w-5 font-bold">
                            <svg
                              width="23"
                              height="26"
                              viewBox="0 0 23 26"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                                fill="#90A3BF"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  {/* 1NOW-11896: Bonzah never reissues the PDF after a date change. */}
                  {bonzahCoverNotes(bookingDetails?.bonzah_cover).map((note, index) => (
                    <p key={index} className="mt-3 text-sm text-text-color">
                      {note}
                    </p>
                  ))}
                </div>
                <div className="mb-6 md:mb-0">
                  <div className="flex justify-between items-center mb-8">
                    <h2 className="text-lg font-bold text-text-color">
                      Renter Info
                    </h2>
                    {!unpaidReservation && bookingDetails?.trip_status !== "cancelled" &&
                      bookingDetails?.cancellation_pending !== true && (
                        <p
                          className="text-lg text-text-color cursor-pointer"
                          onClick={() => setIsRenterInfoEdit(!isRenterInfoEdit)}
                        >
                          {isRenterInfoEdit ? "Cancel" : "Edit"}
                        </p>
                      )}
                  </div>
                  {!isRenterInfoEdit ? (
                    <>
                      <div className="mb-4 flex ">
                        <div>
                          <p className="text-text-color ">
                            {bookingDetails?.customer?.first_name}{" "}
                            {bookingDetails?.customer?.last_name}
                          </p>
                        </div>
                      </div>
                      <div className="mb-4 flex">
                        <div>
                          <p className="text-text-color ">
                            {bookingDetails?.customer?.email}
                          </p>
                        </div>
                      </div>
                      <div className="mb-4 flex ">
                        <div>
                          <p className="text-text-color ">
                            <PhoneNumberDisplay
                              phone={bookingDetails?.customer?.phone_no}
                            />
                          </p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <RenterInfo
                      bookingDetails={bookingDetails}
                      setIsRenterInfoEdit={setIsRenterInfoEdit}
                      onSuccessfulUpdate={fetchBookingDetail}
                    />
                  )}
                  {!unpaidReservation && (<>
                  {identityLink ? (
                    <div className="mb-4 flex justify-between items-center">
                      <div>
                        <p className="text-text-color  truncate max-w-48">
                          {identityLink}
                        </p>
                      </div>
                      <span
                        className={`mx-2 cursor-pointer font-semibold ${COPY_LINK_CLS}`}
                        onClick={() => {
                          navigator.clipboard.writeText(identityLink);
                          toast.success("Link Copied", { id: COPY_TOAST_ID });
                        }}
                      >
                        Copy
                      </span>
                    </div>
                  ) : (
                    <div className="mb-4 flex justify-between items-center">
                      <div>
                        <p className="text-text-color">ID Verification</p>
                      </div>
                      <span
                        className={`mx-2 cursor-pointer font-semibold ${bookingDetails?.customer?.identity_verified
                          ? VERIFIED_CLS
                          : PENDING_CLS
                          }`}
                        {...(!bookingDetails?.customer?.identity_verified && {
                          onClick: handlePrimaryDriverVerificationButtonClick,
                        })}
                      >
                        <div className="flex">
                          {bookingDetails &&
                            bookingDetails?.customer?.identity_verified !== true
                            ? <PendingChip label="Create verification link" />
                            : "Verified"}
                          {bookingDetails?.customer?.identity_verified !==
                            true && (
                              <svg
                                width="23"
                                height="26"
                                viewBox="0 0 23 26"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                                  fill="#90A3BF"
                                />
                              </svg>
                            )}
                        </div>
                      </span>
                    </div>
                  )}

                  {canopyLink ? (
                    <div className="mb-4 flex justify-between items-center">
                      <div>
                        <p className="text-text-color truncate max-w-48">
                          {canopyLink}
                        </p>
                      </div>
                      <span
                        className={`mx-2 cursor-pointer font-semibold ${COPY_LINK_CLS}`}
                        onClick={() => {
                          navigator.clipboard.writeText(canopyLink);
                          toast.success("Link Copied", { id: COPY_TOAST_ID });
                        }}
                      >
                        Copy
                      </span>
                    </div>
                  ) : (
                    <div className="mb-4 flex justify-between items-center">
                      <div>
                        <p className="text-text-color">
                          Insurance Verification
                        </p>
                      </div>
                      <span
                        className={`mx-2 cursor-pointer font-semibold ${bookingDetails?.customer?.insurance_verified
                          ? VERIFIED_CLS
                          : PENDING_CLS
                          }`}
                        {...(!bookingDetails?.customer?.insurance_verified && {
                          onClick: handleCanopyVerificationButtonClick,
                        })}
                      >
                        <div className="flex">
                          {bookingDetails &&
                            bookingDetails?.customer?.insurance_verified !== true
                            ? <PendingChip label="Create verification link" />
                            : "Verified"}
                          {bookingDetails?.customer?.insurance_verified !==
                            true && (
                              <svg
                                width="23"
                                height="26"
                                viewBox="0 0 23 26"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                                  fill="#90A3BF"
                                />
                              </svg>
                            )}
                        </div>
                      </span>
                    </div>
                  )}

                  {bookingDetails?.secondary_driver_obj && (
                    <>
                      {driveridentityLink ? (
                        <div className="mb-4 flex justify-between items-center">
                          <div>
                            <p className="text-text-color truncate max-w-48">
                              {driveridentityLink}
                            </p>
                          </div>
                          <span
                            className={`mx-2 cursor-pointer font-semibold ${COPY_LINK_CLS}`}
                            onClick={() => {
                              navigator.clipboard.writeText(driveridentityLink);
                              toast.success("Link Copied", { id: COPY_TOAST_ID });
                            }}
                          >
                            Copy
                          </span>
                        </div>
                      ) : (
                        <div className="mb-4 flex justify-between items-center">
                          <div>
                            <p className="text-text-color">
                              Secondary Driver ID Verification
                            </p>
                          </div>
                          <span
                            className={`mx-2 cursor-pointer font-semibold ${bookingDetails.secondary_driver_obj
                              .identity_verified
                              ? VERIFIED_CLS
                              : PENDING_CLS
                              }`}
                            {...(!bookingDetails.secondary_driver_obj
                              .identity_verified && {
                              onClick:
                                handleDriverPrimaryDriverVerificationButtonClick,
                            })}
                          >
                            <div className="flex">
                              {bookingDetails.secondary_driver_obj
                                .identity_verified
                                ? "Verified"
                                : <PendingChip label="Create verification link" />}
                              {!bookingDetails.secondary_driver_obj
                                .identity_verified && (
                                  <svg
                                    width="23"
                                    height="26"
                                    viewBox="0 0 23 26"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                                      fill="#90A3BF"
                                    />
                                  </svg>
                                )}
                            </div>
                          </span>
                        </div>
                      )}

                      {drivercanopyLink ? (
                        <div className="mb-4 flex justify-between items-center">
                          <div>
                            <p className="text-text-color truncate max-w-48">
                              {drivercanopyLink}
                            </p>
                          </div>
                          <span
                            className={`mx-2 cursor-pointer font-semibold ${COPY_LINK_CLS}`}
                            onClick={() => {
                              navigator.clipboard.writeText(drivercanopyLink);
                              toast.success("Link Copied", { id: COPY_TOAST_ID });
                            }}
                          >
                            Copy
                          </span>
                        </div>
                      ) : (
                        <div className="mb-4 flex justify-between items-center">
                          <div>
                            <p className="text-text-color">
                              Secondary Driver Insurance Verification
                            </p>
                          </div>
                          <span
                            className={`mx-2 cursor-pointer font-semibold ${bookingDetails.secondary_driver_obj
                              .insurance_verified
                              ? VERIFIED_CLS
                              : PENDING_CLS
                              }`}
                            {...(!bookingDetails.secondary_driver_obj
                              .insurance_verified && {
                              onClick:
                                handleDriverCanopyVerificationButtonClick,
                            })}
                          >
                            <div className="flex">
                              {bookingDetails.secondary_driver_obj
                                .insurance_verified
                                ? "Verified"
                                : <PendingChip label="Create verification link" />}
                              {!bookingDetails.secondary_driver_obj
                                .insurance_verified && (
                                  <svg
                                    width="23"
                                    height="26"
                                    viewBox="0 0 23 26"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                                      fill="#90A3BF"
                                    />
                                  </svg>
                                )}
                            </div>
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  </>)}
                </div>
              </div>
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="w-full mt-4">
                    <h2 className="text-lg font-bold my-4 text-text-color">
                      About The Car
                    </h2>
                    <div className="mb-4">
                      <p className="text-text-color truncater">
                        {bookingDetails?.fleet?.name}
                      </p>
                    </div>
                    <div className="mb-4 flex justify-between">
                      <p className="text-text-color">License plate number</p>
                      <span className="font-bold text-text-color">
                        {bookingDetails?.fleet?.plate_number}
                      </span>
                    </div>
                    {specText(bookingDetails?.fleet?.color) && (
                      <div className="mb-4 flex justify-between">
                        <p className="text-text-color">Car color</p>
                        <span className="font-bold text-text-color">
                          {specText(bookingDetails?.fleet?.color)}
                        </span>
                      </div>
                    )}
                    {specText(bookingDetails?.fleet?.make) && (
                      <div className="mb-4 flex justify-between">
                        <span className="text-text-color">Build</span>
                        <span className="font-bold text-text-color truncate w-44 text-right">
                          {specText(bookingDetails?.fleet?.make)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {!bookingDetails?.note ? (
                  ""
                ) : (
                  <div className="w-full mt-12">
                    <h2 className="text-xl font-bold mb-8 text-text-color">
                      Note
                    </h2>
                    <span className="text-text-color whitespace-pre-wrap break-words">
                      {bookingDetails.note
                        .split(/(\s+)/)
                        .map((part: string, index: number) => {
                          const urlRegex = /^(https?:\/\/[^\s]+)$/;
                          if (urlRegex.test(part.trim())) {
                            return (
                              <a
                                key={index}
                                href={part.trim()}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {part.trim()}
                              </a>
                            );
                          }
                          return part;
                        })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!unpaidReservation && (insuranceDocuments.length > 0 ||
            bookingDetails?.custom_insurance_options?.[0]?.upload_after_payment === true) && (
              <div className="mt-8 border border-secondary-100 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-text-color mb-5">
                  Upload Proof Of Insurance
                </h2>

                {bookingDetails?.custom_insurance_options?.[0]?.upload_after_payment === true && (
                  <div
                    {...getInsuranceRootProps()}
                    className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl py-8 px-4 cursor-pointer transition-colors mb-4 ${isInsuranceDragActive
                      ? "border-primary-brand bg-blue-50"
                      : "border-secondary-100 hover:border-primary-brand"
                      }`}
                  >
                    <input {...getInsuranceInputProps()} />
                    <div className="w-12 h-12 rounded-full border-2 border-secondary-200 flex items-center justify-center mb-2">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="#9AA3AD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <polyline points="17 8 12 3 7 8" stroke="#9AA3AD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <line x1="12" y1="3" x2="12" y2="15" stroke="#9AA3AD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-text-color">
                      {isInsuranceDragActive ? "Drop files here…" : "Drop or select file"}
                    </span>
                    <span className="text-xs text-neutral-400 mt-1">Supported: PDF. Max: 10MB</span>
                  </div>
                )}


                {pendingInsuranceFiles.map((file, i) => (
                  <div
                    key={`pending-${i}`}
                    className="flex items-center gap-3 border border-secondary-100 rounded-lg px-4 py-3 mb-2"
                  >
                    <div className="pending-attention flex-shrink-0 w-10 h-10 bg-amber-50 rounded-md flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#d97706" opacity="0.2" />
                        <path d="M14 2v6h6M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="#d97706" strokeWidth="1.5" fill="none" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-color truncate">{file.name}</p>
                      <p className="text-xs text-neutral-400 mt-0.5">
                        {(file.size / (1024 * 1024)).toFixed(1)}MB · Ready to upload
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingInsuranceFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-red-50 text-neutral-400 hover:text-red-500 text-xs transition-colors"
                    >
                      &#x2715;
                    </button>
                  </div>
                ))}

                {/* Already uploaded files */}
                {insuranceDocuments.map((doc: any) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-3 border border-secondary-100 rounded-lg px-4 py-3 mb-2"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-secondary-50 rounded-md flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#64748b" opacity="0.2" />
                        <path d="M14 2v6h6M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="#64748b" strokeWidth="1.5" fill="none" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-color truncate">{doc.name ?? doc.file_name ?? "Insurance Document"}</p>
                      <p className="text-xs text-neutral-400 mt-0.5">Uploaded Successfully</p>
                    </div>
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-brand flex items-center justify-center">
                      <CheckIcon className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                ))}


                {pendingInsuranceFiles.length > 0 && (
                  <button
                    type="button"
                    onClick={handleInsuranceUpload}
                    disabled={insuranceUploading}
                    className="mt-3 w-full py-3 rounded-xl bg-primary-brand text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
                  >
                    {insuranceUploading
                      ? `Uploading... ${insuranceUploadProgress}%`
                      : `Upload ${pendingInsuranceFiles.length} file${pendingInsuranceFiles.length > 1 ? "s" : ""}`}
                  </button>
                )}
              </div>
            )}


          {!unpaidReservation && (
          <div className="mt-8 border border-secondary-100 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-text-color mb-5">
              Upload ID Documents
            </h2>


            <div
              {...getIdRootProps()}
              className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl py-8 px-4 cursor-pointer transition-colors mb-4 ${isIdDragActive
                ? "border-primary-brand bg-blue-50"
                : "border-secondary-100 hover:border-primary-brand"
                }`}
            >
              <input {...getIdInputProps()} />
              <div className="w-12 h-12 rounded-full border-2 border-secondary-200 flex items-center justify-center mb-2">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="#9AA3AD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="17 8 12 3 7 8" stroke="#9AA3AD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="12" y1="3" x2="12" y2="15" stroke="#9AA3AD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="text-sm font-medium text-text-color">
                {isIdDragActive ? "Drop files here…" : "Drop or select file"}
              </span>
              <span className="text-xs text-neutral-400 mt-1">Supported: Images, PDF. Max: 10MB</span>
            </div>


            {pendingIdFiles.map((file, i) => (
              <div
                key={`pending-id-${i}`}
                className="flex items-center gap-3 border border-secondary-100 rounded-lg px-4 py-3 mb-2"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#3182ce" opacity="0.2" />
                    <path d="M14 2v6h6M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="#3182ce" strokeWidth="1.5" fill="none" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-color truncate">{file.name}</p>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    {(file.size / (1024 * 1024)).toFixed(1)}MB · Ready to upload
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPendingIdFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-red-50 text-neutral-400 hover:text-red-500 text-xs transition-colors"
                >
                  &#x2715;
                </button>
              </div>
            ))}


            {idDocuments.map((doc: any) => (
              <div
                key={doc.id}
                className="flex items-center gap-3 border border-secondary-100 rounded-lg px-4 py-3 mb-2"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#3182ce" opacity="0.2" />
                    <path d="M14 2v6h6M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="#3182ce" strokeWidth="1.5" fill="none" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-color truncate">{doc.name ?? doc.file_name ?? "ID Document"}</p>
                  <p className="text-xs text-neutral-400 mt-0.5">Uploaded Successfully</p>
                </div>
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-brand flex items-center justify-center">
                  <CheckIcon className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
            ))}

            {pendingIdFiles.length > 0 && (
              <button
                type="button"
                onClick={handleIdUpload}
                disabled={idUploading}
                className="mt-3 w-full py-3 rounded-xl bg-primary-brand text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {idUploading
                  ? `Uploading... ${idUploadProgress}%`
                  : `Upload ${pendingIdFiles.length} file${pendingIdFiles.length > 1 ? "s" : ""}`}
              </button>
            )}
          </div>
          )}

          {!unpaidReservation && (<>
          {isLongTerm(
            ourCompany,
            bookingDetails?.is_long_term_rental === true,
            bookingDetails?.long_term_rental_nature,
            bookingDetails?.long_term_rental_duration,
          ) && (
            <div className="mt-8">
              <ConnectedBookings bookingDetails={bookingDetails ?? []} />
            </div>
          )}
          <div className="mt-12">
            <PreviousBookings
              booking_id={bookingId ?? ""}
              cancellationPolicy={cancellationPolicy}
              privacyPolicy={privacyPolicy}
            />
          </div>
          </>)}
        </div>
      </>
    );
  };

  // 1NOW-10442: one shape per field — a label line, then a fixed-height value
  // band. The status badge, the plain-text values and the action buttons all
  // centre on that band, so the labels sit on one line and everything below
  // them on another. The actions column is `auto`-width instead of a fifth
  // equal column: at 1/5 the two buttons were wider than their own cell and
  // spilled left over the renter name.
  const renderCarDetailsSection = () => {
    const labelCls = "mb-2 text-sm font-medium text-text-color";
    const valueCls =
      "flex min-h-10 items-center text-base font-semibold text-text-color";
    const actionBtnCls =
      "h-10 min-w-32 px-4 text-sm font-medium border border-[#263238] ttnc-ButtonPrimary !rounded-md disabled:bg-opacity-70 bg-primary-6000 hover:bg-primary-700 text-on-primary hover:ring-primary-6000 hover:ring-offset-2 hover:ring-2";
    return (
      <div>
        <div className="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
          <div className="flex min-w-0 flex-col">
            <span className={labelCls}>Booking Status</span>
            <span className="flex min-h-10 items-center">
              <Badge
                className="h-8 max-w-fit items-center whitespace-nowrap px-4"
                color={
                  // 1NOW-1169: surface a pending modification/cancellation request
                  // as the status (cancellation wins), shown like trip status
                  // instead of a separate banner.
                  bookingDetails?.cancellation_pending
                    ? "red"
                    : bookingDetails?.modification_pending
                      ? "yellow"
                      : bookingDetails?.trip_status === "pending"
                        ? "yellow"
                        : bookingDetails?.trip_status === "cancelled"
                          ? "red"
                          : "green"
                }
                name={
                  bookingDetails?.cancellation_pending
                    ? "Cancellation Pending"
                    : bookingDetails?.modification_pending
                      ? "Modification Pending"
                      : bookingDetails?.trip_status
                        ? bookingDetails.trip_status.charAt(0).toUpperCase() +
                        bookingDetails.trip_status.slice(1)
                        : ""
                }
              />
            </span>
          </div>
          <div className="flex min-w-0 flex-col">
            <span className={labelCls}>Booking ID</span>
            <span className={valueCls}># {bookingDetails?.id}</span>
          </div>
          <div className="flex min-w-0 flex-col">
            <span className={labelCls}>Vehicle Name</span>
            <span className={valueCls}>
              <span className="truncate">{bookingDetails?.fleet?.name}</span>
            </span>
          </div>
          <div className="flex min-w-0 flex-col">
            <span className={labelCls}>Renter Name</span>
            <span className={valueCls}>
              <span className="truncate">
                {bookingDetails?.customer?.first_name}{" "}
                {bookingDetails?.customer?.last_name}
              </span>
            </span>
          </div>
          {!unpaidReservation && bookingDetails.drop_off_time &&
            bookingDetails.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            new Date(bookingDetails?.drop_off_time).getTime() +
            24 * 60 * 60 * 1000 >
            Date.now() && (
              <div className="flex min-w-0 flex-col sm:col-span-2 lg:col-span-1">
                {/* Stands in for the label line so the buttons land on the
                    same band as the values on desktop; hidden once the
                    actions drop onto their own row. */}
                <span
                  aria-hidden="true"
                  className={`${labelCls} hidden lg:block`}
                >
                  &nbsp;
                </span>
                <div className="flex min-h-10 flex-col items-stretch gap-2 sm:flex-row lg:items-center lg:justify-end">
                  {bookingDetails.trip_status !== "completed" && (
                    <button
                      className={actionBtnCls}
                      onClick={() => {
                        setIsCancelModalOpen(true);
                      }}
                    >
                      Cancel Booking
                    </button>
                  )}

                  <button
                    className={actionBtnCls}
                    onClick={() => {
                      setIsEdit(!isEdit);
                    }}
                  >
                    {isEdit ? "Cancel Changes" : "Edit Booking"}
                  </button>
                </div>
              </div>
            )}
        </div>
      </div>
    );
  };

  const renderCancelBookingSection = () => {
    return (
      <div className="py-8 w-full">
        <h2 className="text-lg font-bold mb-8 text-text-color">Trip Info</h2>
        <div className="mb-4 flex justify-between items-center">
          <div>
            <p className="text-text-color">Total Bill</p>
            <p className="text-text-color">{totalBill}</p>
          </div>

          <div
            onClick={handleOpenTotalBillModal}
            className="text-text-color cursor-pointer mx-2"
          >
            <div className="flex text-text-color font-semibold">
              View Invoice
              <svg
                width="23"
                height="26"
                viewBox="0 0 23 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                  fill="#90A3BF"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="mb-4 flex justify-between items-center">
          <div>
            <p className="text-text-color">Agreement</p>
          </div>
          <div
            onClick={openRentalAgreement}
            className={`cursor-pointer mx-2 ${COPY_LINK_CLS}`}
          >
            <div className="flex font-semibold">
              View Agreement
              <svg
                width="23"
                height="26"
                viewBox="0 0 23 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.5253 19.7222C13.3481 19.7222 13.1709 19.6488 13.031 19.4917C12.7605 19.1879 12.7605 18.685 13.031 18.3812L18.1977 12.5774L13.031 6.77361C12.7605 6.4698 12.7605 5.96694 13.031 5.66313C13.3014 5.35932 13.7491 5.35932 14.0195 5.66313L19.6806 12.0222C19.951 12.326 19.951 12.8288 19.6806 13.1327L14.0195 19.4917C13.8797 19.6488 13.7025 19.7222 13.5253 19.7222Z"
                  fill="#90A3BF"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className="w-full flex flex-col space-y-8 ">
        {/* A cancelled reservation (or one being cancelled) cannot be paid: no notice, and no wait for a link. */}
        {unpaidReservation && bookingDetails?.trip_status !== "cancelled" && !bookingDetails?.cancellation_pending && (
          <UnpaidNotice booking={bookingDetails} total={totalBill} onBooking={setBookingDetail} />
        )}
        {renderImgSection()}
        {renderCarDetailsSection()}

        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
      </div>
    );
  };

  const rendernoBooking = () => {
    return (
      <div className="min-h-[60vh]">
        <p>Booking Not Found</p>
      </div>
    );
  };
  return (
    <>
      {bookingloading ? (
        <div className="flex justify-center items-center col-span-full py-96">
          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
        </div>
      ) : (
        <div className={`nc-PayPage ${className}`} data-nc-id="PayPage">
          {/* 1NOW-1169: a pending modification/cancellation request is now
              surfaced through the Booking Status badge (shown like trip
              status), so the previous pending banners were removed. */}

          {/* 1NOW-1170: concrete pending-modification view — what changed
              (old → new), the price delta, and what will be charged on
              approval (from the backend `pending_modification` summary). */}
          {bookingDetails?.pending_modification && (
            <div className="container mt-4">
              <div className="max-w-6xl mx-auto rounded-md sm:rounded-xl border border-yellow-300 bg-yellow-50 p-3 sm:p-5">
                <div className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-600 flex-shrink-0" />
                  <p className="font-bold text-yellow-900 text-sm sm:text-lg">
                    Modification pending — under review
                  </p>
                </div>
                {bookingDetails.pending_modification.approval_note && (
                  <p className="text-yellow-900 font-semibold text-xs sm:text-sm mt-1">
                    {bookingDetails.pending_modification.approval_note}
                  </p>
                )}
                <p className="text-yellow-800 text-xs sm:text-sm mt-1">
                  Your requested changes are awaiting approval. Here's what will
                  change and what you'll be charged once it's approved.
                </p>

                {bookingDetails.pending_modification.changes?.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {bookingDetails.pending_modification.changes.map(
                      (change: any, idx: number) => (
                        <div key={idx} className="text-xs sm:text-sm">
                          <span className="font-semibold text-gray-700">
                            {change.label}:{" "}
                          </span>
                          <span className="text-gray-600 line-through">
                            {change.old}
                          </span>
                          <span className="mx-1.5 text-gray-400">→</span>
                          <span className="text-gray-900 font-medium">
                            {change.new}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                )}

                {bookingDetails.pending_modification.price && (
                  <div className="mt-3 border-t border-yellow-200 pt-3 text-xs sm:text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Current total</span>
                      <span className="text-gray-900">
                        $
                        {formatPrice(
                          bookingDetails.pending_modification.price.old_total,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">New total</span>
                      <span className="font-medium text-gray-900">
                        $
                        {formatPrice(
                          bookingDetails.pending_modification.price.new_total,
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-gray-900">
                      <span>
                        {bookingDetails.pending_modification.price.difference < 0
                          ? "Refund"
                          : "Additional charge"}
                      </span>
                      <span>
                        $
                        {formatPrice(
                          Math.abs(
                            bookingDetails.pending_modification.price.difference,
                          ),
                        )}
                      </span>
                    </div>
                  </div>
                )}

                {bookingDetails.pending_modification.charge?.amount > 0 && (
                  <p className="mt-3 text-xs sm:text-sm text-yellow-900">
                    {bookingDetails.pending_modification.charge.direction ===
                    "refund"
                      ? "A refund of "
                      : "A charge of "}
                    <span className="font-semibold">
                      $
                      {formatPrice(
                        bookingDetails.pending_modification.charge.amount,
                      )}
                    </span>
                    {bookingDetails.pending_modification.charge.card_last_four
                      ? ` to your card ···· ${bookingDetails.pending_modification.charge.card_last_four}`
                      : ""}{" "}
                    will be applied {bookingDetails.pending_modification.charge.when}.
                  </p>
                )}
              </div>
            </div>
          )}

          {!unpaidReservation &&
            bookingDetails?.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            !identityLink &&
            bookingDetails &&
            bookingDetails?.customer?.identity_verified !== true && (
              <>
                <div className="min-h-22 flex items-center container mt-4">
                  <div className="pending-banner pending-attention flex flex-row w-full items-center justify-between gap-3 max-w-6xl mx-auto p-3 sm:p-4 rounded-lg">
                    <span className="pending-banner-text font-semibold text-xs sm:text-sm md:text-base leading-snug">
                    Verify your{" "}
                      <span className="font-extrabold">identity</span> to
                      confirm your booking
                    </span>
                    <ButtonPrimary
                      onClick={handlePrimaryDriverVerificationButtonClick}
                      className="pending-banner-cta min-w-16 max-w-20 sm:max-w-none !text-[10px] sm:!text-[14px] font-semibold md:py-2 px-3 rounded-lg whitespace-nowrap h-[34px] md:h-[40px]"
                    >
                      {identityLoading ? (
                        <div className="flex justify-center items-center col-span-full">
                          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                        </div>
                      ) : (
                        <>
                          <span className="block sm:hidden">Create link</span>
                          <span className="hidden sm:block">
                            Create verification link
                          </span>
                        </>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </>
            )}
          {identityLink && (
            <div className="min-h-22 flex items-center container mt-4">
              <div className="pending-banner pending-attention flex flex-row w-full items-center justify-between gap-3 max-w-6xl mx-auto p-3 sm:p-4 rounded-lg">
                <span className="pending-banner-text font-medium text-[12px] sm:text-sm md:text-base break-words truncate">
                  {identityLink}
                </span>
                <div className="space-x-0 md:space-x-4 flex flex-col md:flex-row space-y-4 md:space-y-0">
                  {/* <button className="ttnc-ButtonPrimary disabled:bg-opacity-70 bg-none border text-sm border-[#FF0000] text-[#FF0000] font-bold py-3 px-4 sm:px-6 rounded-xl">
                    Purchase Insurance
                  </button> */}
                  <ButtonPrimary
                    onClick={() => {
                      navigator.clipboard.writeText(identityLink);
                      toast.success("Link Copied", { id: COPY_TOAST_ID });
                    }}
                    className="pending-banner-cta min-w-20 md:ml-2 text-[12px] font-semibold rounded-lg px-4 py-3 whitespace-nowrap"
                  >
                    Copy
                  </ButtonPrimary>
                  <ButtonPrimary
                    onClick={() => window.open(identityLink, "_blank")}
                    className="pending-banner-cta min-w-20 md:ml-2 text-[12px] font-semibold rounded-lg px-4 py-3 whitespace-nowrap"
                  >
                    Open
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          )}
          {!unpaidReservation &&
            bookingDetails?.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            !canopyLink &&
            bookingDetails &&
            bookingDetails?.customer?.insurance_verified !== true && (
              <div className="min-h-22 flex items-center container mt-4">
                <div className="pending-banner pending-attention flex flex-row w-full items-center justify-between gap-3 max-w-6xl mx-auto p-3 sm:p-4 rounded-lg">
                  <span className="pending-banner-text font-semibold text-xs sm:text-sm md:text-base leading-snug">
                    Verify your{" "}
                    <span className="font-extrabold">insurance</span> to confirm
                    your booking
                  </span>
                  <div className="space-x-4 flex-col md:flex-row space-y-4">
                    <ButtonPrimary
                      onClick={handleCanopyVerificationButtonClick}
                      className="pending-banner-cta min-w-16 max-w-20 sm:max-w-none !text-[10px] sm:!text-[14px] font-semibold md:py-2 px-3 rounded-lg whitespace-nowrap h-[34px] md:h-[40px]"
                    >
                      {canopyLoading ? (
                        <div className="flex justify-center items-center col-span-full">
                          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                        </div>
                      ) : (
                        <>
                          <span className="block sm:hidden">Create link</span>
                          <span className="hidden sm:block">
                            Create verification link
                          </span>
                        </>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            )}
          {canopyLink && (
            <div className="min-h-22 flex items-center container mt-4">
              <div className="pending-banner pending-attention flex flex-row w-full items-center justify-between gap-3 max-w-6xl mx-auto p-3 sm:p-4 rounded-lg">
                <span className="pending-banner-text font-medium text-[12px] sm:text-sm md:text-base break-words truncate">
                  {canopyLink}
                </span>
                <div className="space-x-0 md:space-x-4 flex flex-col md:flex-row space-y-4 md:space-y-0">
                  {/* <button className="ttnc-ButtonPrimary disabled:bg-opacity-70 bg-none border text-sm border-[#FF0000] text-[#FF0000] font-bold py-3 px-4 sm:px-6 rounded-xl">
                    Purchase Insurance
                  </button> */}
                  <ButtonPrimary
                    onClick={() => {
                      navigator.clipboard.writeText(canopyLink);
                      toast.success("Link Copied", { id: COPY_TOAST_ID });
                    }}
                    className="pending-banner-cta min-w-20 md:ml-2 text-[12px] font-semibold rounded-lg px-4 py-3 whitespace-nowrap"
                  >
                    Copy
                  </ButtonPrimary>
                  <ButtonPrimary
                    onClick={() => window.open(canopyLink, "_blank")}
                    className="pending-banner-cta min-w-20 md:ml-2 text-[12px] font-semibold rounded-lg px-4 py-3 whitespace-nowrap"
                  >
                    Open
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          )}
          {!unpaidReservation &&
            !driveridentityLink &&
            bookingDetails?.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            bookingDetails &&
            bookingDetails?.secondary_driver_obj?.identity_verified !== null &&
            bookingDetails?.secondary_driver_obj?.identity_verified ===
            false && (
              <>
                <div className="min-h-22 flex items-center container mt-4">
                  <div className="pending-banner pending-attention flex flex-row w-full items-center justify-between gap-3 max-w-6xl mx-auto p-3 sm:p-4 rounded-lg">
                    <span className="pending-banner-text font-semibold text-xs sm:text-sm md:text-base leading-snug">
                    Verify your{" "}
                      <span className="font-extrabold">
                        {" "}
                        secondary driver identity
                      </span>{" "}
                      to confirm your booking
                    </span>
                    <ButtonPrimary
                      onClick={handleDriverPrimaryDriverVerificationButtonClick}
                      className="pending-banner-cta min-w-16 max-w-20 sm:max-w-none !text-[10px] sm:!text-[14px] font-semibold md:py-2 px-3 rounded-lg whitespace-nowrap h-[34px] md:h-[40px]"
                    >
                      {driveridentityLoading ? (
                        <div className="flex justify-center items-center col-span-full">
                          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                        </div>
                      ) : (
                        <>
                          <span className="block sm:hidden">Create link</span>
                          <span className="hidden sm:block">
                            Create verification link
                          </span>
                        </>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </>
            )}
          {driveridentityLink && (
            <div className="min-h-22 flex items-center container mt-4">
              <div className="pending-banner pending-attention flex flex-row w-full items-center justify-between gap-3 max-w-6xl mx-auto p-3 sm:p-4 rounded-lg">
                <span className="pending-banner-text font-medium text-[12px] sm:text-sm md:text-base break-words truncate">
                  {driveridentityLink}
                </span>
                <div className="space-x-0 md:space-x-4 flex flex-col md:flex-row space-y-4 md:space-y-0">
                  {/* <button className="ttnc-ButtonPrimary disabled:bg-opacity-70 bg-none border text-sm border-[#FF0000] text-[#FF0000] font-bold py-3 px-4 sm:px-6 rounded-xl">
                    Purchase Insurance
                  </button> */}
                  <ButtonPrimary
                    onClick={() => {
                      navigator.clipboard.writeText(driveridentityLink);
                      toast.success("Link Copied", { id: COPY_TOAST_ID });
                    }}
                    className="pending-banner-cta min-w-20 md:ml-2 text-[12px] font-semibold rounded-lg px-4 py-3 whitespace-nowrap"
                  >
                    Copy
                  </ButtonPrimary>
                  <ButtonPrimary
                    onClick={() => window.open(driveridentityLink, "_blank")}
                    className="pending-banner-cta min-w-20 md:ml-2 text-[12px] font-semibold rounded-lg px-4 py-3 whitespace-nowrap"
                  >
                    Open
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          )}
          {!unpaidReservation &&
            !drivercanopyLink &&
            bookingDetails?.trip_status !== "cancelled" &&
            bookingDetails?.cancellation_pending !== true &&
            bookingDetails &&
            bookingDetails?.secondary_driver_obj !== null &&
            bookingDetails?.secondary_driver_obj?.insurance_verified ===
            false && (
              <div className="min-h-22 flex items-center container mt-4">
                <div className="pending-banner pending-attention flex flex-row w-full items-center justify-between gap-3 max-w-6xl mx-auto p-3 sm:p-4 rounded-lg">
                  <span className="pending-banner-text font-semibold text-xs sm:text-sm md:text-base leading-snug">
                    Verify your{" "}
                    <span className="font-extrabold">
                      secondary driver insurance
                    </span>{" "}
                    to confirm your booking
                  </span>
                  <div className="space-x-4 flex-col md:flex-row space-y-4">
                    <ButtonPrimary
                      onClick={handleDriverCanopyVerificationButtonClick}
                      className="pending-banner-cta min-w-16 max-w-20 sm:max-w-none !text-[10px] sm:!text-[14px] font-semibold md:py-2 px-3 rounded-lg whitespace-nowrap h-[34px] md:h-[40px]"
                    >
                      {drivercanopyLoading ? (
                        <div className="flex justify-center items-center col-span-full">
                          <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                        </div>
                      ) : (
                        <>
                          <span className="block sm:hidden">Create link</span>
                          <span className="hidden sm:block">
                            Create verification link
                          </span>
                        </>
                      )}
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            )}
          {drivercanopyLink && (
            <div className="min-h-22 flex items-center container mt-4">
              <div className="pending-banner pending-attention flex flex-row w-full items-center justify-between gap-3 max-w-6xl mx-auto p-3 sm:p-4 rounded-lg">
                <span className="pending-banner-text font-medium text-[12px] sm:text-sm md:text-base break-words truncate">
                  {drivercanopyLink}
                </span>
                <div className="space-x-0 md:space-x-4 flex flex-col md:flex-row space-y-4 md:space-y-0">
                  <ButtonPrimary
                    onClick={() => {
                      navigator.clipboard.writeText(drivercanopyLink);
                      toast.success("Link Copied", { id: COPY_TOAST_ID });
                    }}
                    className="pending-banner-cta min-w-20 md:ml-2 text-[12px] font-semibold rounded-lg px-4 py-3 whitespace-nowrap"
                  >
                    Copy
                  </ButtonPrimary>
                  <ButtonPrimary
                    onClick={() => window.open(drivercanopyLink, "_blank")}
                    className="pending-banner-cta min-w-20 md:ml-2 text-[12px] font-semibold rounded-lg px-4 py-3 whitespace-nowrap"
                  >
                    Open
                  </ButtonPrimary>
                </div>
              </div>
            </div>
          )}
          <Transition appear show={isOpen}>
            <Dialog
              as="div"
              className="relative z-10 focus:outline-none border"
              onClose={close}
            >
              <div className="fixed inset-0 z-10 w-screen overflow-y-auto border border-primary-6000">
                <div className="flex min-h-full items-center justify-center p-4">
                  <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 transform-[scale(95%)]"
                    enterTo="opacity-100 transform-[scale(100%)]"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 transform-[scale(100%)]"
                    leaveTo="opacity-0 transform-[scale(95%)]"
                  >
                    <DialogPanel className="w-full max-w-md bg-white rounded-x p-6 backdrop-blur-2xl rounded-md  border border-primary-6000">
                      <div className="flex justify-between mb-6">
                        <DialogTitle as="h3" className="font-medium text-xl">
                          Modify Trip
                        </DialogTitle>
                        <button onClick={close}>
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>

                      <ButtonPrimary
                        className="bg-primary-500 w-full mb-5"
                        onClick={() => {
                          modifyBooking(bookingId!);
                        }}
                      >
                        Modify Booking
                      </ButtonPrimary>
                    </DialogPanel>
                  </TransitionChild>
                </div>
              </div>
            </Dialog>
          </Transition>
          <Transition appear show={isTotalBillModalOpen}>
            <Dialog as="div" className="relative z-50" onClose={() => { }}>
              <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 transform scale-95"
                    enterTo="opacity-100 transform scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 transform scale-100"
                    leaveTo="opacity-0 transform scale-95"
                  >
                    <DialogPanel className="w-full max-w-md bg-bg-color border border-secondary-100 p-6 rounded-md shadow-lg !z-50">
                      <div className="flex justify-between mb-4 border-b border-secondary-100 pb-2">
                        <DialogTitle as="h2" className="text-xl font-bold">
                          Invoice
                        </DialogTitle>
                        <button onClick={handleCloseTotalBillModal}>
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                      <Invoice invoice={bookingDetails?.invoice} carName={bookingDetails?.fleet?.name} />
                    </DialogPanel>
                  </TransitionChild>
                </div>
              </div>
            </Dialog>
          </Transition>

          <main className="container mt-11 mb-24 lg:mb-32 ">
            <div className="max-w-6xl mx-auto">
              {bookingloading ? (
                <div className="flex justify-center items-center col-span-full py-96">
                  <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                </div>
              ) : bookingDetails !== null ? (
                <>
                  {renderContent()}
                  {isEdit && !unpaidReservation && (
                    <EditBooking
                      startTime={startTime}
                      setStartTime={setStartTime}
                      endTime={endTime}
                      setEndTime={setEndTime}
                      bookingDetails={bookingDetails}
                      fetchBookingDetail={fetchBookingDetail}
                      onClose={handleCloseEdit}
                    />
                  )}
                  {bookingDetails?.trip_status !== "cancelled" &&
                    bookingDetails?.cancellation_pending !== true
                    ? renderBookingDetailsSection()
                    : renderCancelBookingSection()}
                </>
              ) : (
                rendernoBooking()
              )}
            </div>
          </main>
        </div>
      )}

      <Transition appear show={isCancellationOpen} as={Fragment}>
        <Dialog
          as="div"
          className="HeroSearchFormMobile__Dialog relative z-50"
          onClose={() => {
            setIsCancellationOpen(false);
          }}
          static={true}
        >
          <div className="fixed inset-0 bg-bg-color">
            <div className="flex h-full">
              <TransitionChild
                as={Fragment}
                enter="ease-out transition-transform"
                enterFrom="opacity-0 translate-y-52"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in transition-transform"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-52"
              >
                <DialogPanel className="relative h-full w-full overflow-hidden flex-1 flex flex-col justify-between ">
                  <>
                    <div className="absolute left-4 top-4 z-0">
                      <button
                        className="focus:outline-none focus:ring-0"
                        onClick={close}
                      >
                        <XMarkIcon
                          onClick={() => {
                            setIsCancellationOpen(false);
                            setIsCancelModalOpen(true);
                          }}
                          className="w-5 h-5 text-text-color"
                        />
                      </button>
                    </div>

                    <div className="flex-1 pt-12 p-1 flex flex-col overflow-auto">
                      <div className="flex-1 flex flex-col bg-card-color z-10">
                        <div className="flex-1 flex flex-col transition-opacity animate-[myblur_0.4s_ease-in-out] overflow-auto">
                          <Cancellation terms={cancellationPolicy} />
                        </div>
                      </div>
                    </div>
                    <div className=" justify-end px-4 py-3 bg-card-color border-t border-secondary-100 flex">
                      <ButtonPrimary
                        sizeClass="px-6 py-3 !rounded-lg"
                        onClick={() => {
                          setIsCancellationOpen(false);
                          setCancellationPolicyChecked(true);
                          setIsCancelModalOpen(true);
                        }}
                      >
                        Accept
                      </ButtonPrimary>
                    </div>
                  </>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={isCancelModalOpen}>
        <Dialog as="div" className="relative z-50" onClose={() => { }}>
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 transform-[scale(95%)]"
                enterTo="opacity-100 transform-[scale(100%)]"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 transform-[scale(100%)]"
                leaveTo="opacity-0 transform-[scale(95%)]"
              >
                <DialogPanel className="w-full max-w-md bg-card-color rounded-xl p-6 shadow-2xl">
                  <div className="flex justify-between items-center mb-4">
                    <DialogTitle
                      as="h3"
                      className="text-xl font-bold text-text-color"
                    >
                      Cancel Booking
                    </DialogTitle>
                    <button
                      onClick={() => setIsCancelModalOpen(false)}
                      className="text-secondary-text-color hover:text-text-color"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <p className="text-secondary-text-color mb-6">
                    Are you sure you want to cancel your booking? This action
                    cannot be undone.
                  </p>

                  <div className="flex gap-2 mt-4 p-4 border border-secondary-100 rounded-md overflow-hidden z-10">
                    <Checkbox
                      checked={cancellationPolicyChecked}
                      onChange={(checked) => {
                        if (checked) {
                          setIsCancellationOpen(true);
                          setIsCancelModalOpen(false);
                        } else {
                          setCancellationPolicyChecked(false);
                          setIsCancelModalOpen(true);
                        }
                      }}
                      className="group size-7 rounded-md p-1 ring-1 ring-primary-brand data-[checked]:bg-primary-brand data-[checked]:bg-opacity-20 ring-inset  mr-4"
                    >
                      <CheckIcon
                        className={`size-5 stroke-primary-brand ${cancellationPolicyChecked ? "visible" : "invisible"
                          }`}
                      />
                    </Checkbox>
                    <div>
                      <div className="text-secondary-text-color text-lg font-medium">
                        I hereby confirm that I have read, understood, and
                        accept the{" "}
                        <span
                          className="text-primary-6000 text-xl font-bold cursor-pointer underline"
                          onClick={() => {
                            setIsCancellationOpen(true);
                            setIsCancelModalOpen(false);
                          }}
                        >
                          Cancellation Policy.
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end mt-6">
                    <ButtonPrimary
                      onClick={() => setIsCancelModalOpen(false)}
                      disabled={isCancelling}
                      className="!border border-secondary-100 !bg-transparent !rounded-lg hover:!border-primary-6000 text-text-color"
                    >
                      No
                    </ButtonPrimary>
                    <ButtonPrimary
                      onClick={handleCancelBooking}
                      className="!border-none !rounded-lg"
                    >
                      {isCancelling ? (
                        <div className="flex items-center gap-2">
                          <div className="spinner-border animate-spin inline-block w-4 h-4 border-2 rounded-full border-t-transparent border-white"></div>
                        </div>
                      ) : (
                        "Yes"
                      )}
                    </ButtonPrimary>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      <TripInspectionModal
        open={isTripInspectionOpen}
        onClose={() => setIsTripInspectionOpen(false)}
        bookingId={bookingUid}
        bookingLabel={{
          vehicle: bookingDetails?.fleet?.name,
          plate: bookingDetails?.fleet?.plate_number,
          reference: bookingDetails?.id,
        }}
        preOdometerImage={preOdometerImage}
        postOdometerImage={postOdometerImage}
        preOdometerLoading={preOdometerLoading}
        postOdometerLoading={postOdometerLoading}
        preOdometerResult={preOdometerResult}
        postOdometerResult={postOdometerResult}
        preOdometerError={preOdometerError}
        postOdometerError={postOdometerError}
        onUploadOdometer={handleOdometerUpload}
        initialPhase={tripInspectionInitialPhase}
        onPhotosChanged={async () => {
          if (!bookingUid) return;
          try {
            const result = await getTripPhotos(bookingUid);
            const photos: any[] = Array.isArray(result)
              ? result
              : Array.isArray(result?.data)
                ? result.data
                : [];
            setPreTripBreakdown(classifyTripPhotos(photos, "pre_trip"));
            setPostTripBreakdown(classifyTripPhotos(photos, "post_trip"));
          } catch {
            // ignore — modal already shows toast on its own errors
          }
        }}
      />
    </>
  );
};

export default BookingDetailPage;
